import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Link, SelectorItem } from '@app/component/selector/selector.component';
import { CreditOptionDto } from '@app/model/credit/credit-option.dto';
import { RouteComponent } from '@app/route/route.component';
import { LoanOption } from '@app/route/shared/LoanOption';
import { CreditDecisionService } from '@app/service/credit-decision/credit-decision.service';
import { CreditService } from '@app/service/credit/credit.service';
import { HeaderService } from '@app/service/header/header.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import { StateService } from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import Translations from './fi-invoice-confirm.component.json';
import { FiUserService } from '@app/service/fi-user-service/fi-user.service';
import { PaymentMethod } from '@app/model/payment-request/payment-request.dto';

@Component({
  selector: 'fi-invoice-confirm',
  templateUrl: './fi-invoice-confirm.component.html',
  styleUrls: ['../../page-shared.css', './fi-invoice-confirm.component.scss'],
})
export class FiInvoiceConfirmComponent
  extends RouteComponent
  implements OnInit
{
  modalActive: boolean;

  public loading: boolean = true;
  public navigationInProgress: boolean;
  public fallbackOffer: CreditOptionDto;

  selectorItems: SelectorItem[];
  selectedItem: SelectorItem;
  selectedInstallmentOption: LoanOption = null;

  translations: { [key: string]: string };

  public authInProgress: boolean = false;
  public authComplete: boolean = false;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected creditService: CreditService,
    protected creditDecisionService: CreditDecisionService,
    protected queryParamsService: QueryParamsService,
    private fiUserService: FiUserService,
    private valuePipe: ValuePipe
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    this.translations =
      Translations[
        this.fiUserService.getCurrentStateLocaleBaseCode(this.state.language)
      ];
    await this.setup();
  }

  async setup(): Promise<void> {
    if (
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT) ||
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT_DIRECT_DEBIT)
    ) {
      this.selectedInstallmentOption = this.state.loanOptions.find(
        (option) => option.termId === this.state.loanOfferId
      );
    }

    this.prepareModalInformation();
    this.loading = false;
  }

  async handleCtaClick(): Promise<void> {
    this.loading = true;

    try {
      await this.fiUserService.commit();
    } catch (err) {
      this.handleError(err);
    }

    await this.navigate();
    this.loading = false;
  }

  toggleModalSelectDetailsTab(): void {
    this.selectedItem = this.selectorItems[0];
    this.toggleModal();
  }

  toggleModalSelectTermsTab(): void {
    this.selectedItem = this.selectorItems[1];
    this.toggleModal();
  }

  toggleModal(): void {
    this.modalActive = !this.modalActive;
  }

  getTermsString(): string {
    if (this.getIsPaymentMethodMatching(PaymentMethod.INVOICE)) {
      return this.translations.modalInvoiceTermsString;
    } else if (
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT) ||
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT_DIRECT_DEBIT)
    ) {
      return this.fiUserService.buildLoanTermsString(
        this.fiUserService.transformDtoTOVM(
          this.selectedInstallmentOption,
          this.toggleModal.bind(this),
          this.state.language
        ),
        this.state.totalCheckoutValue,
        this.state.language
      );
    }
  }

  getModalTermsOfPaymentTitle(): string {
    if (this.getIsPaymentMethodMatching(PaymentMethod.INVOICE)) {
      return this.translations.modalTitleTermsOfPaymentInvoice;
    } else if (
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT) ||
      this.getIsPaymentMethodMatching(PaymentMethod.CREDIT_DIRECT_DEBIT)
    ) {
      return this.translations.modalTitleTermsOfPaymentCredit;
    }
  }

  prepareModalInformation(): void {
    const activeLoanOption: LoanOption =
      this.selectedInstallmentOption !== null
        ? this.selectedInstallmentOption
        : this.state.loanOptions[0];

    this.selectorItems = [
      {
        tab: this.translations.modalTabSummary,
        title: this.translations.modalTitleSummary,
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `${
          this.translations.modalTotalAmountIncVat
        } ${this.valuePipe.transform(this.state.totalCheckoutValue)}`,
      },
      {
        tab: this.translations.modalTabTermsOfPayment,
        title: this.getModalTermsOfPaymentTitle(),
        descriptions: [
          {
            text: this.translations.calculations + this.getTermsString(),
          },
        ],
        links: this.getLoanOptionLinks(activeLoanOption),
      },
    ];
  }

  getIsPaymentMethodMatching(paymentMethod: PaymentMethod | string): boolean {
    return this.state.paymentInformation.paymentMethod === paymentMethod;
  }

  openURLinNewWindow(url: string): void {
    window.open(url, '_blank');
  }

  getInstallmentsTitle(): string {
    return this.translations.installmentsTitle
      .replace(
        '${MONTHLYCOST}',
        this.selectedInstallmentOption.monthlyCost.toString().replace('.', ',')
      )
      .replace(
        '${NUMBEROFMONTHS}',
        this.selectedInstallmentOption.numberOfMonths.toString()
      );
  }

  getLoanOptionLinks(activeLoanOption: LoanOption): Link[] {
    let links: Link[] = [];

    if (activeLoanOption.secciURL) {
      links.push({
        text: this.translations.modalLinkSecci,
        onClick: () => this.openURLinNewWindow(activeLoanOption.secciURL),
      });
    }

    if (activeLoanOption.loanTermsURL) {
      links.push({
        text: this.translations.modalLinkCreditAgreement,
        onClick: () => this.openURLinNewWindow(activeLoanOption.loanTermsURL),
      });
    }

    return links;
  }
}
